import { faCameraRetro, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import axios from 'axios'
import classNames from 'classnames'
import React, { useCallback, useContext, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { PresignedURLResponse } from '../../pages/api/upload-url'
import Context from '../../utils/Context'
import { DiaperBankPublic } from '../../utils/DiaperBankDTO'
import Button from '../form/Button'
import FormFieldInput from '../form/FormFieldInput'
import FormSwitch from '../form/FormSwitch'
import Modal from '../Modal'
import { toastMessage } from '../toastMessage'
import { handleBadFiles } from './Photos'

export interface NewUpdateArgs {
  text: string
  sharePublicly: boolean
  imageKey?: string
}

export default function AddUpdate({ diaperBank, open, onClose }: { diaperBank: DiaperBankPublic; open: boolean; onClose: () => void }) {
  const context = useContext(Context)
  const { register, handleSubmit, formState, setValue, watch, reset } = useForm<NewUpdateArgs>({ mode: 'all', defaultValues: { sharePublicly: true } })
  const [uploadedFile, setUploadedFile] = useState<File>()
  const [uploadedFilePreviewURL, setUploadedFilePreviewURL] = useState<string>()
  const [isPosting, setPosting] = useState(false)

  const { mutateAsync, isLoading } = useMutation(
    (update: NewUpdateArgs) => {
      return context.apiClient.post<DiaperBankPublic>(`diaperBanks/${diaperBank.slug}/updates`, { update })
    },
    {
      onSuccess(data) {
        context.queryClient.setQueryData([`diaperBank`, diaperBank.slug], data)
      },
    },
  )

  const deletePhoto = () => {
    setUploadedFile(undefined)
    setUploadedFilePreviewURL(undefined)
    setValue('imageKey', undefined)
  }

  const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles) => {
    if (!!rejectedFiles?.length) {
      setUploadedFile(undefined)
      return
    }

    try {
      const acceptedFile = acceptedFiles[0]
      setUploadedFile(acceptedFile)
      setUploadedFilePreviewURL(URL.createObjectURL(acceptedFile))
    } catch (error) {
      console.error(error)
      setUploadedFile(undefined)
    } finally {
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    onDropRejected: handleBadFiles,
    accept: 'image/jpeg, image/png',
    maxFiles: 1,
    multiple: false,
    maxSize: 7 * 1024 * 1024,
  })

  const onSubmit = async ({ text, sharePublicly }: NewUpdateArgs) => {
    try {
      setPosting(true)
      let imageKey: string | undefined = undefined
      if (uploadedFile) {
        const { presignedPutUrl, fileKey, displayUrl } = (await context.apiClient.get<PresignedURLResponse>(`upload-url?type=${uploadedFile.type}?diaperBankId=${diaperBank.id}`))
          .data
        imageKey = fileKey
        await axios.put(presignedPutUrl, uploadedFile, { headers: { 'Content-Type': uploadedFile.type } })
      }

      await mutateAsync({ text, sharePublicly, imageKey })
      reset()
      setUploadedFilePreviewURL(undefined)
      setUploadedFile(undefined)
      toastMessage({ title: 'Update has been saved', type: 'success' })
      onClose()
    } catch (error) {
      console.error(error)
      toastMessage({ title: 'There was an error saving your update', type: 'error' })
    } finally {
      setPosting(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title='Post Update'>
      <div className='p-4'>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div {...getRootProps()}>
            <div className={classNames('bg-gray-100 rounded-md border-gray-300 border my-2 p-4 text-center', { 'bg-gray-300': isDragActive })}>
              <input {...getInputProps()} />
              <div className='font-light text-gray-700'>
                <FontAwesomeIcon icon={faCameraRetro} className='inline-block mr-2' /> {uploadedFilePreviewURL ? 'Replace' : 'Upload'} Image
              </div>
            </div>
            {uploadedFilePreviewURL && (
              <div className='my-6'>
                <img src={uploadedFilePreviewURL} className='w-full border border-gray-400 p-1' />
              </div>
            )}
          </div>
          {uploadedFilePreviewURL && (
            <div className='text-right mt-4 outline-none'>
              <button onClick={() => deletePhoto()} className='font-thin text-sm'>
                <FontAwesomeIcon icon={faTrash} className='h-4 w-4 inline mr-2' aria-hidden={true} />
                <span>Delete</span>
              </button>
            </div>
          )}

          <FormFieldInput label='Message' register={register('text', { required: true })} required errors={formState.errors} autoFocus type='textarea' rows={6} />
          <FormSwitch label='Share the post on your public timeline?' onChange={(val) => setValue('sharePublicly', val, { shouldDirty: true })} defaultValue={true} />

          <Button type='submit' fullWidth className='mt-4' loading={isPosting} color='primary'>
            <>Save {watch('sharePublicly') ? '& Publish' : ''}</>
          </Button>
          <div className='my-4 text-xs'>All subscribed contributors will get your update via email</div>
        </form>
      </div>
    </Modal>
  )
}
